import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getExperts, createExpert, deleteExpert } from "@/lib/api";
import { Plus, Trash2, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

export default function AdminExperts() {
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ department: "", count: 0 });
  const [saving, setSaving] = useState(false);

  const fetchExperts = async () => {
    try {
      const res = await getExperts();
      setExperts(res.data);
    } catch (error) {
      toast.error("Failed to load experts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExperts();
  }, []);

  const handleSave = async () => {
    if (!formData.department || formData.count < 0) {
      toast.error("Please fill in department and count");
      return;
    }

    setSaving(true);
    try {
      await createExpert(formData);
      toast.success("Expert count saved successfully");
      setDialogOpen(false);
      setFormData({ department: "", count: 0 });
      fetchExperts();
    } catch (error) {
      toast.error("Failed to save expert count");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this department?")) return;
    try {
      await deleteExpert(id);
      toast.success("Department deleted successfully");
      fetchExperts();
    } catch (error) {
      toast.error("Failed to delete department");
    }
  };

  const totalExperts = experts.reduce((sum, e) => sum + e.count, 0);

  return (
    <AdminLayout title="Technical Experts">
      <div className="space-y-6" data-testid="admin-experts">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-600">Manage technical experts by department</p>
            <p className="text-sm text-slate-500 mt-1">Total: {totalExperts} experts</p>
          </div>
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-[#0066CC] hover:bg-[#0052A3]"
            data-testid="add-expert-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Department
          </Button>
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Department</TableHead>
                  <TableHead className="w-32">Count</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                    </TableCell>
                  </TableRow>
                ) : experts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8 text-slate-500">
                      No departments found. Click "Add Department" to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  experts.map((expert) => (
                    <TableRow key={expert.id} className="data-table-row">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center text-amber-500">
                            <Users className="h-5 w-5" />
                          </div>
                          <span className="font-medium">{expert.department}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-2xl font-bold text-[#0066CC]">{expert.count}</span>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(expert.id)}
                          className="text-slate-600 hover:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add/Update Department</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Department Name</Label>
                <Input
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="e.g., ERP Consultants"
                  data-testid="department-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Number of Experts</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.count}
                  onChange={(e) => setFormData({ ...formData, count: parseInt(e.target.value) || 0 })}
                  data-testid="count-input"
                />
              </div>
              <p className="text-sm text-slate-500">
                If department already exists, the count will be updated.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#0066CC] hover:bg-[#0052A3]"
                data-testid="save-expert-btn"
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
